import React, { useState, useEffect } from 'react';
import { Pencil, Camera, Images, QrCode } from 'lucide-react';
import { useInventory, InventoryItem } from '../store/inventory';
import { useGroups } from '../store/groups';
import { useAuth } from '../store/auth';
import { useOrganizations } from '../store/organizations';
import { BottomSheet, PrimaryBtn, SecondaryBtn, Field, Input, Select, Textarea, ErrorMsg, QuantityStepper, ExpandSection, TagInput, CameraCapture, UnitPicker } from './UI';
import { CategoryIcon, CategoryIconPicker } from '../lib/categoryIcons';
import { sanitizeText } from '../lib/validation';
import { QRCodeSheet } from './QRCodeSheet';

const ENHETER = ['stk', 'kg', 'g', 'liter', 'dl', 'ml', 'meter', 'cm', 'mm', 'm²', 'm³', 'rull', 'pakke', 'brett', 'boks', 'tube', 'sekk', 'kasse', 'sett', 'par'];

interface Props {
  item: InventoryItem;
  onClose: () => void;
  onSaved?: () => void;
  onManageCategories?: () => void;
}

export const EditItemSheet: React.FC<Props> = ({ item, onClose, onSaved, onManageCategories }) => {
  const {
    items, lists, updateItem, uploadImage, assignToList,
    fetchInventoryGroups, addInventoryToGroup, removeInventoryFromGroup,
    fetchLoanableItems, fetchItems,
    fetchItemFilterCategories, addItemFilterCategory, removeItemFilterCategory,
    savedTags, addSavedTags,
    itemCodes, setItemCode,
  } = useInventory();
  const { categories, groups, currentGroup } = useGroups();
  const { user } = useAuth();
  const { organizations, myAdminOrgIds, setItemOrganization, orgGroups, fetchOrgGroups, groupOrgInfo } = useOrganizations();

  const [navn, setNavn] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [antall, setAntall] = useState('');
  const [enhet, setEnhet] = useState('stk');
  const [customEnhet, setCustomEnhet] = useState(false);
  const [beskrivelse, setBeskrivelse] = useState('');
  const [emoji, setEmoji] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loanable, setLoanable] = useState(false);
  const [organizationId, setOrganizationId] = useState('');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [filterCategoryIds, setFilterCategoryIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    setNavn(item.navn);
    setTags(item.personal_tags ?? []);
    setAntall(String(item.antall));
    setEnhet(item.enhet);
    setCustomEnhet(!ENHETER.includes(item.enhet));
    setBeskrivelse(item.beskrivelse);
    setEmoji(item.emoji ?? '');
    setImageFile(null);
    setImagePreview(item.bilde_url || null);
    setLoanable(item.loanable ?? false);
    setOrganizationId(item.organization?.id || '');
    setFilterCategoryIds((item.filterCategories ?? []).map(c => c.id));
    setCode(itemCodes.find(c => c.inventoryId === item.id)?.code || '');
    setCodeError('');
    setError('');
    fetchInventoryGroups(item.id).then(setGroupIds);
    fetchItemFilterCategories(item.id).then(cats => setFilterCategoryIds(cats.map(c => c.id)));
    if (items.length === 0 && user?.id) fetchItems(user.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const brukerTagger = [...new Set([
    ...savedTags,
    ...items.flatMap(i => i.personal_tags ?? []).filter(Boolean),
  ])].sort();
  const currentListId = items.find(i => i.id === item.id)?.list_id ?? null;
  const qrInfo = currentListId === null
    ? { code, title: item.navn }
    : {
        code: itemCodes.find(c => c.listId === currentListId)?.code || '',
        title: lists.find(l => l.id === currentListId)?.name || item.navn,
      };

  const handleSave = async () => {
    if (!navn.trim()) { setError('Navn er påkrevd'); return; }
    const antallNum = antall.trim() === '' ? 0 : parseInt(antall);
    if (isNaN(antallNum) || antallNum < 0) { setError('Antall må være et tall (0 eller mer)'); return; }
    if (loanable && groupIds.length === 0) { setError('Velg minst én gruppe tingen skal vises i'); return; }
    setSaving(true); setError('');
    const bilde_url = imageFile ? (await uploadImage(imageFile)) || '' : (imagePreview || '');
    await updateItem(item.id, {
      navn: sanitizeText(navn, 100),
      personal_tags: tags,
      antall: antallNum,
      enhet,
      beskrivelse: sanitizeText(beskrivelse, 300),
      bilde_url,
      emoji,
      loanable,
    });
    // Skriv kun til organization_items hvis eierskapet faktisk er endret —
    // unngår et unødvendig RLS-avslag hvis brukeren ikke lenger er org-admin
    // for en organisasjon tingen allerede (uendret) er koblet til.
    if (organizationId !== (item.organization?.id || '')) {
      const orgErr = await setItemOrganization(item.id, organizationId || null);
      if (orgErr) { setError(orgErr); setSaving(false); return; }
    }
    if (currentListId === null) {
      const codeErr = await setItemCode(user!.id, item.id, code.trim() || null);
      if (codeErr) { setCodeError(codeErr); setSaving(false); return; }
    }
    const [prevGroups, prevFilterCats] = await Promise.all([
      fetchInventoryGroups(item.id),
      fetchItemFilterCategories(item.id),
    ]);
    const prevFilterCatIds = prevFilterCats.map(c => c.id);
    await Promise.all([
      ...prevGroups.filter(g => !groupIds.includes(g)).map(g => removeInventoryFromGroup(item.id, g)),
      ...groupIds.filter(g => !prevGroups.includes(g)).map(g => addInventoryToGroup(item.id, g)),
      ...prevFilterCatIds.filter(id => !filterCategoryIds.includes(id)).map(id => removeItemFilterCategory(item.id, id)),
      ...filterCategoryIds.filter(id => !prevFilterCatIds.includes(id)).map(id => addItemFilterCategory(item.id, id)),
    ]);
    const curGrpId = currentGroup()?.id;
    if (curGrpId) fetchLoanableItems(curGrpId);
    if (user?.id) {
      if (tags.length > 0) await addSavedTags(user.id, tags);
      fetchItems(user.id);
    }
    setSaving(false);
    onSaved?.();
    onClose();
  };

  const pillStyle = (active: boolean, _color: 'green' | 'blue' = 'green') => ({
    padding: '5px 13px', borderRadius: 30, fontSize: 13, flexShrink: 0 as const, fontWeight: active ? 600 : 400,
    border: `1px solid ${active ? 'var(--green)' : 'var(--border-strong)'}`,
    background: active ? 'var(--green)' : 'var(--bg)',
    color: active ? '#fff' : 'var(--text2)',
    cursor: 'pointer' as const,
  });

  return (
    <BottomSheet title="Rediger ting" onClose={onClose}>
      {error && <ErrorMsg msg={error} />}

      {/* ── Bildekort (hero) ── */}
      {!imagePreview ? (
        <div style={{ border: '2px dashed var(--border-strong)', borderRadius: 'var(--radius-sm)', padding: '22px 16px 18px', textAlign: 'center', marginBottom: 16, background: 'var(--surface)' }}>
          <Camera size={30} strokeWidth={1.4} style={{ color: 'var(--text3)', marginBottom: 8 }} />
          <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14, lineHeight: 1.5 }}>
            Legg til et bilde av tingen
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setCameraOpen(true)}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg)', color: 'var(--text2)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
              <Camera size={15} strokeWidth={2} /> Ta bilde
            </button>
            <label style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg)', color: 'var(--text2)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
              <Images size={15} strokeWidth={2} /> Velg bilde
              <input type="file" accept="image/*" onChange={e => {
                const f = e.target.files?.[0];
                if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); }
              }} style={{ display: 'none' }} />
            </label>
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <div style={{ position: 'relative' }}>
            <img src={imagePreview} alt="Forhåndsvisning" style={{ width: '100%', borderRadius: 'var(--radius-sm)', objectFit: 'cover', maxHeight: 180, display: 'block' }} />
            <label style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 12, padding: '5px 11px', borderRadius: 20, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Images size={12} strokeWidth={2} /> Bytt bilde
              <input type="file" accept="image/*" onChange={e => {
                const f = e.target.files?.[0];
                if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); }
              }} style={{ display: 'none' }} />
            </label>
          </div>
        </div>
      )}

      {/* ── Alltid synlig ── */}
      <Field label={<>Navn <span style={{ color: 'var(--red)', fontWeight: 700 }}>*</span></>}>
        <Input
          placeholder="F.eks. Skruer M6"
          value={navn}
          onChange={e => setNavn(e.target.value)}
          autoFocus
        />
      </Field>

      <Field label="Beskrivelse (valgfritt)">
        <Textarea placeholder="Kort beskrivelse..." value={beskrivelse}
          onChange={e => setBeskrivelse(e.target.value)} rows={2} />
      </Field>

      <Field label="Tagger">
        <TagInput
          value={tags}
          onChange={setTags}
          suggestions={brukerTagger}
          placeholder="F.eks. Garasje, Boden, Verktøy..."
        />
      </Field>

      {/* ── Antall og enhet ── */}
      <ExpandSection label="Antall og enhet" defaultOpen={item.antall > 0}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text2)', marginBottom: 5 }}>Antall</label>
            <QuantityStepper value={antall} onChange={setAntall} />
          </div>
          <div style={{ width: 110 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text2)', marginBottom: 5 }}>Enhet</label>
            <UnitPicker
              value={enhet}
              options={ENHETER}
              onChange={val => {
                if (val === '__custom__') { setCustomEnhet(true); setEnhet(''); }
                else { setCustomEnhet(false); setEnhet(val); }
              }}
            />
          </div>
        </div>
        {customEnhet && (
          <Input placeholder="Skriv enhet (f.eks. palle, bunt, løpemeter...)"
            value={enhet} onChange={e => setEnhet(e.target.value)} style={{ marginTop: 10 }} autoFocus />
        )}
      </ExpandSection>

      {myAdminOrgIds.size > 0 && (
        <Field label="Hvem eier dette?">
          <Select value={organizationId} onChange={async e => {
            const orgId = e.target.value;
            setOrganizationId(orgId);
            if (orgId) {
              setLoanable(true);
              await fetchOrgGroups(orgId);
              const orgGroupIds = (useOrganizations.getState().orgGroups[orgId] || []).map(g => g.id);
              setGroupIds(orgGroupIds);
            } else {
              setLoanable(false);
              setGroupIds([]);
              setFilterCategoryIds([]);
            }
          }}>
            <option value="">Deg</option>
            {organizations.filter(o => myAdminOrgIds.has(o.id)).map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </Select>
        </Field>
      )}

      {(() => { const pickableGroups = organizationId ? (orgGroups[organizationId] || []) : groups;
        // Seksjoner pillene etter organisasjon når du eier tingen selv («Deg») —
        // du kan være med i grupper fra flere ulike organisasjoner samtidig.
        // Ikke aktuelt når organizationId er satt, siden pickableGroups da allerede
        // er begrenset til den ene valgte organisasjonens egne grupper.
        const groupSections = organizationId ? null : (() => {
          const personal = pickableGroups.filter(g => !groupOrgInfo[g.id]);
          const byOrg = new Map<string, { name: string; items: typeof pickableGroups }>();
          pickableGroups.forEach(g => {
            const org = groupOrgInfo[g.id];
            if (!org) return;
            if (!byOrg.has(org.id)) byOrg.set(org.id, { name: org.name, items: [] });
            byOrg.get(org.id)!.items.push(g);
          });
          return [{ label: 'Dine grupper', items: personal }, ...Array.from(byOrg.values()).map(v => ({ label: v.name, items: v.items }))].filter(s => s.items.length > 0);
        })();
        return (
      <>
      {/* ── Utlån ── */}
      <ExpandSection key={organizationId ? 'utlan-org' : 'utlan-default'} label="Utlån" defaultOpen={item.loanable === true || !!organizationId}>
        <button type="button" onClick={() => { const next = !loanable; setLoanable(next); if (!next) { setGroupIds([]); setFilterCategoryIds([]); } else if (groupIds.length === 0) { setGroupIds(pickableGroups.map(g => g.id)); } }}
          style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 14 }}>
          <div style={{ width: 44, height: 24, borderRadius: 12, background: loanable ? 'var(--green)' : 'var(--border-strong)', transition: 'background 0.2s', position: 'relative', flexShrink: 0 }}>
            <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: loanable ? 22 : 2, transition: 'left 0.2s' }} />
          </div>
          <span style={{ fontSize: 14, color: 'var(--text2)' }}>{loanable ? 'Ja – vises i Oversikt' : 'Nei – kun synlig for deg'}</span>
        </button>

        {loanable && (
          <>
            {categories.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Filterkategori i Oversikt</div>
                  {onManageCategories && (
                    <button type="button" onClick={onManageCategories}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 30, border: '1px solid var(--border)', background: 'none', color: 'var(--text3)', fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>
                      <Pencil size={10} strokeWidth={2} />
                      Rediger
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {categories.map(c => {
                    const active = filterCategoryIds.includes(c.id);
                    return (
                      <button key={c.id} type="button"
                        onClick={() => setFilterCategoryIds(ids => active ? ids.filter(id => id !== c.id) : [...ids, c.id])}
                        style={{ ...pillStyle(active), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        {c.emoji && <CategoryIcon emoji={c.emoji} size={13} color={active ? '#fff' : 'var(--text2)'} />}
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: groupIds.length === 0 ? 'var(--red)' : 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>
                Vis i grupper <span style={{ color: 'var(--red)' }}>*</span>
              </div>
              {pickableGroups.length > 0 ? (
                <>
                  {groupSections ? (
                    groupSections.map(section => (
                      <div key={section.label} style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>{section.label}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {section.items.map(g => (
                            <button key={g.id} type="button"
                              onClick={() => setGroupIds(ids => ids.includes(g.id) ? ids.filter(id => id !== g.id) : [...ids, g.id])}
                              style={pillStyle(groupIds.includes(g.id))}>{g.name}</button>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {pickableGroups.map(g => (
                        <button key={g.id} type="button"
                          onClick={() => setGroupIds(ids => ids.includes(g.id) ? ids.filter(id => id !== g.id) : [...ids, g.id])}
                          style={pillStyle(groupIds.includes(g.id))}>{g.name}</button>
                      ))}
                    </div>
                  )}
                  {groupIds.length === 0 && (
                    <div style={{ marginTop: 7, fontSize: 12, color: 'var(--red)' }}>Velg minst én gruppe for å dele tingen</div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--text3)' }}>
                  {organizationId ? 'Organisasjonen har ingen tilkoblede grupper enda.' : 'Du er ikke med i noen grupper enda.'}
                </div>
              )}
            </div>
          </>
        )}
      </ExpandSection>
      </>
      ); })()}

      <ExpandSection label="Liste og ikon" defaultOpen={false}>
        <Field label="Ikon">
          <CategoryIconPicker value={emoji} onChange={setEmoji} />
        </Field>
        {lists.length > 0 && (
          <Field label="Liste (valgfritt)">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <button type="button" onClick={() => assignToList(item.id, null)}
                style={pillStyle(currentListId === null, 'blue')}>Ingen liste</button>
              {lists.map(l => {
                const active = currentListId === l.id;
                return (
                  <button key={l.id} type="button" onClick={() => assignToList(item.id, l.id)}
                    style={{ ...pillStyle(active, 'blue'), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    {l.emoji && <CategoryIcon emoji={l.emoji} size={13} color={active ? 'var(--blue)' : 'var(--text2)'} />}
                    {l.name}
                  </button>
                );
              })}
            </div>
          </Field>
        )}
        {currentListId === null ? (
          <Field label="Kode (valgfritt)">
            <Input placeholder="F.eks. 7 eller B12" value={code}
              onChange={e => { setCode(e.target.value.toUpperCase().slice(0, 12)); setCodeError(''); }}
              style={{ fontFamily: 'monospace', letterSpacing: 2 }} />
            {codeError && <ErrorMsg msg={codeError} />}
            {itemCodes.find(c => c.inventoryId === item.id) && (
              <button type="button" onClick={() => setShowQr(true)}
                style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: 'var(--green)', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                <QrCode size={14} strokeWidth={2} /> Vis QR-kode
              </button>
            )}
          </Field>
        ) : (
          (() => {
            const listCode = itemCodes.find(c => c.listId === currentListId);
            const listName = lists.find(l => l.id === currentListId)?.name || 'listen';
            return listCode ? (
              <div style={{ fontSize: 13, color: 'var(--text2)' }}>
                Ligger i esken «{listName}» — kode: <strong style={{ fontFamily: 'monospace', letterSpacing: 1 }}>{listCode.code}</strong>
                <button type="button" onClick={() => setShowQr(true)}
                  style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: 'var(--green)', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                  <QrCode size={14} strokeWidth={2} /> Vis QR-kode
                </button>
              </div>
            ) : (
              <p style={{ fontSize: 12, color: 'var(--text3)' }}>
                Denne tingen ligger i esken «{listName}» — sett en kode på esken (Administrer lister i Inventar) for å kunne finne den igjen med QR/kode.
              </p>
            );
          })()
        )}
      </ExpandSection>

      <PrimaryBtn onClick={handleSave} disabled={saving}>{saving ? 'Lagrer...' : 'Lagre endringer'}</PrimaryBtn>
      <SecondaryBtn onClick={onClose}>Avbryt</SecondaryBtn>

      {cameraOpen && (
        <CameraCapture
          onCapture={file => { setImageFile(file); setImagePreview(URL.createObjectURL(file)); setCameraOpen(false); }}
          onClose={() => setCameraOpen(false)}
        />
      )}
      {showQr && <QRCodeSheet code={qrInfo.code} title={qrInfo.title} onClose={() => setShowQr(false)} />}
    </BottomSheet>
  );
};
